import React, { useEffect, useState } from 'react'
import { fetchWrapper } from '../../helpers'
import { LuCheck, LuX } from 'react-icons/lu';
import {
    Box,
    Flex,
    Spinner,
    Heading,
    Text,
    VStack,
    HStack,
    Tag,
    Wrap,
    WrapItem,
    Icon
} from '@chakra-ui/react'
import { toast } from 'react-toastify';

const humanizeKey = (key) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

// Backend returns free-form HTML for `message` (comma lists, <br/>-separated
// key:value lines, single words). Flatten it into short chip-sized fragments
// instead of rendering raw HTML, so every check looks consistent.
const htmlToChips = (html) => {
    if (!html) return [];
    const withBreaks = html.replace(/<br\s*\/?>/gi, '\n');
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
    const text = doc.body.textContent || '';
    return text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .flatMap((line) =>
            line.includes(':') ? [line] : line.split(',').map((s) => s.trim()).filter(Boolean)
        );
};

const Chip = ({ children, icon, iconColor }) => (
    <HStack
        spacing={1.5}
        bg="white"
        border="1px solid #e5e7eb"
        borderRadius="6px"
        px={2.5}
        py="4px"
    >
        {icon && <Icon as={icon} color={iconColor} boxSize={3.5} />}
        <Text fontSize="12px" color="#374151">{children}</Text>
    </HStack>
);

const Chips = ({ items }) => (
    <Wrap spacing={2}>
        {items.map((item, index) => (
            <WrapItem key={index}>
                <Chip>{item}</Chip>
            </WrapItem>
        ))}
    </Wrap>
);

const DetailChips = ({ entries }) => (
    <Wrap spacing={2}>
        {entries.map(([key, value]) => (
            <WrapItem key={key}>
                <Chip
                    icon={typeof value === 'boolean' ? (value ? LuCheck : LuX) : undefined}
                    iconColor={value ? 'green.500' : 'red.500'}
                >
                    {humanizeKey(key)}
                    {typeof value === 'boolean' ? '' : `: ${value}`}
                </Chip>
            </WrapItem>
        ))}
    </Wrap>
);

const CustomerValidation = ({ id }) => {
    const [validationData, setValidationData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDataReady, setIsDataReady] = useState(false);

    useEffect(() => {
        loadValidationData();
    }, [id]);

    const loadValidationData = async () => {
        setLoading(true);
        setIsDataReady(false);

        try {

            const url = `${process.env.REACT_APP_API_URL}/customer/check-list?customer_id=${id}`;
            const data = await fetchWrapper.get(url);





            setValidationData(data);





            setIsDataReady(true);

        } catch (error) {
            console.error("Error loading channels:", error);
            toast(`Error loading channels`, { type: "error" });
        } finally {
            setLoading(false);
        }
    };


    // Don't render the form until we have all data ready
    if (loading || !isDataReady) {
        return (
            <Box>
                <Box mb={3}>
                    <Heading size="sm" mb={1}>Check Validation</Heading>
                    <Text color="gray.600" fontSize="xs">
                        Automated eligibility and configuration checks for this customer
                    </Text>
                </Box>

                <Flex justify="center" align="center" py={6}>
                    <VStack spacing={2}>
                        <Spinner size="md" color="blue.500" />
                        <Text color="gray.500" fontSize="sm">Loading checks...</Text>
                    </VStack>
                </Flex>
            </Box>
        );
    }

    const checks = validationData?.data || [];

    return (
        <Box>
            <Box mb={3}>
                <Heading size="sm" mb={1}>Check Validation</Heading>
                <Text color="gray.600" fontSize="xs">
                    Automated eligibility and configuration checks for this customer
                </Text>
            </Box>

            {checks.length === 0 ? (
                <Flex justify="center" py={6}>
                    <Text color="gray.500" fontSize="sm">No validation checks available.</Text>
                </Flex>
            ) : (
                <VStack spacing={3} align="stretch">
                    {checks.map((rs, index) => {
                            const messageChips = htmlToChips(rs.message);
                            const hasDetails = rs?.details?.length || rs?.contract_detail?.length;

                            return (
                                <Box
                                    key={index}
                                    border="1px solid #0000001a"
                                    borderRadius="12px"
                                    p={4}
                                    bg="white"
                                    transition="box-shadow 0.2s ease"
                                    _hover={{ boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)' }}
                                >
                                    <Flex justify="space-between" align="center" gap={3} flexWrap="wrap" mb={messageChips.length || hasDetails ? 3 : 0}>
                                        <HStack spacing={3}>
                                            <Flex
                                                align="center"
                                                justify="center"
                                                boxSize="30px"
                                                borderRadius="full"
                                                bg={rs.success ? 'green.50' : 'red.50'}
                                                flexShrink={0}
                                            >
                                                <Icon as={rs.success ? LuCheck : LuX} color={rs.success ? 'green.500' : 'red.500'} boxSize={4} />
                                            </Flex>
                                            <Text fontSize="15px" fontWeight="700" color="#111827">
                                                {rs.description}
                                            </Text>
                                        </HStack>
                                        <Tag
                                            colorScheme={rs.success ? 'green' : 'red'}
                                            borderRadius="6px"
                                            px={3}
                                            py="4px"
                                            fontSize="11px"
                                        >
                                            {rs.success ? 'Passed' : 'Failed'}
                                        </Tag>
                                    </Flex>

                                    {messageChips.length > 0 && (
                                        <Box mb={hasDetails ? 3 : 0}>
                                            <Chips items={messageChips} />
                                        </Box>
                                    )}

                                    <VStack spacing={2} align="stretch">
                                        {rs?.details?.map((dRs, dIndex) => (
                                            <Box key={dIndex} p={3} bg="#f8fafc" borderRadius="10px">
                                                <Text fontWeight="600" fontSize="13px" color="#111827" mb={2}>
                                                    {dRs.channel}{dRs?.account?.name ? ` (${dRs.account.name})` : ''}
                                                </Text>
                                                <VStack spacing={2} align="stretch">
                                                    {dRs?.account?.account_detail?.map((aRs, aIndex) => (
                                                        <DetailChips key={aIndex} entries={Object.entries(aRs)} />
                                                    ))}
                                                </VStack>
                                            </Box>
                                        ))}

                                        {rs?.contract_detail?.map((dRs, dIndex) => (
                                            <Box key={`contract-${dIndex}`} p={3} bg="#f8fafc" borderRadius="10px">
                                                <Text fontWeight="600" fontSize="13px" color="#111827" mb={2}>
                                                    Contract Details
                                                </Text>
                                                <DetailChips entries={Object.entries(dRs)} />
                                            </Box>
                                        ))}
                                    </VStack>
                                </Box>
                            );
                        })}
                    </VStack>
                )}
        </Box>
    );
};

export default CustomerValidation;